import { DateOnly, subMonths } from '@if/core/date'
import { EFinancingProgram, getEProvinceList } from '@if/core/types'
import {
  ActionButton,
  ControlledCellNumberField,
  ControlledDateOnlyPicker,
  ControlledPercentField,
  ControlledSelect,
  ControlledTextField,
  DataField,
  FieldGrid,
  FieldsetCard,
  renderSelectMenuItems,
  useNumberOptionEnum,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import DeleteIconButton from '@src/components/Common/Button/DeleteIconButton'
import { getToday } from '@src/services/DateTimeProvider'
import { getETermList } from '@src/types/constants'
import { useEffect, useRef } from 'react'
import { Control, useFieldArray, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { defaultFundedProducts, firstPaymentFor, TestLoanDraftType, WIZARD_FREQUENCIES } from './test-loan-schema'

interface Props {
  control: Control<TestLoanDraftType>
  setValue: UseFormSetValue<TestLoanDraftType>
}

const LoanParametersStep = ({ control, setValue }: Props) => {
  const { t } = useTranslation()
  const program = useWatch({ control, name: 'financingProgramId' })
  const activation = useWatch({ control, name: 'activationPlannedOn' })
  const { fields, replace, remove } = useFieldArray({ control, name: 'fundedProducts' })
  const programOptions = renderSelectMenuItems(
    [
      { value: EFinancingProgram.Auto, label: t('loan.createWizard.programAuto') },
      { value: EFinancingProgram.IfXpress, label: t('loan.createWizard.programIfx') },
    ],
    (option) => option,
  )
  const termOptions = useNumberOptionEnum(getETermList())
  const frequencyOptions = useTranslatedOptionEnum('EPaymentFrequency', WIZARD_FREQUENCIES, 'enum')
  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')

  // A program change resets the product rows to that program's defaults (not on mount).
  const previousProgram = useRef(program)
  useEffect(() => {
    if (!program || previousProgram.current === program) return
    previousProgram.current = program
    replace(defaultFundedProducts(program))
  }, [program, replace])

  // The first payment follows the activation date (activation + 7 days); it stays editable afterwards.
  const previousActivation = useRef(activation?.toJSON())
  useEffect(() => {
    if (!activation || previousActivation.current === activation.toJSON()) return
    previousActivation.current = activation.toJSON()
    setValue('firstPaymentExpectedOn', firstPaymentFor(activation), { shouldValidate: true })
  }, [activation, setValue])

  const setSixMonthsAgo = () =>
    setValue('activationPlannedOn', new DateOnly(subMonths(getToday(), 6)), { shouldValidate: true })

  return (
    <Stack spacing={2}>
      <FieldsetCard title={t('loan.createWizard.stepParameters')}>
        <Stack spacing={1}>
          <FieldGrid columns={{ xs: 1, sm: 2, lg: 4 }}>
            <ControlledSelect
              control={control}
              name="financingProgramId"
              label={t('loan.createWizard.program')}
              options={programOptions}
            />
            <ControlledSelect
              control={control}
              name="stateIso"
              label={t('loan.createWizard.state')}
              options={provinceOptions}
            />
            <ControlledTextField control={control} name="firstName" label={t('loan.createWizard.firstName')} />
            <ControlledTextField control={control} name="lastName" label={t('loan.createWizard.lastName')} />
            <ControlledPercentField control={control} name="interestRate" label={t('loan.createWizard.interestRate')} />
            <ControlledSelect control={control} name="term" label={t('loan.createWizard.term')} options={termOptions} />
            <ControlledSelect
              control={control}
              name="frequency"
              label={t('loan.createWizard.frequency')}
              options={frequencyOptions}
            />
          </FieldGrid>
          {/* Narrower tracks: a date needs less room than a select. The button rides a label-less
              DataField so it lands on the control band, level with the date inputs. */}
          <FieldGrid columns={{ xs: 1, sm: 3, lg: 6 }}>
            <ControlledDateOnlyPicker
              control={control}
              name="activationPlannedOn"
              label={t('loan.createWizard.activationDate')}
            />
            <ControlledDateOnlyPicker
              control={control}
              name="firstPaymentExpectedOn"
              label={t('loan.createWizard.firstPaymentDate')}
            />
            <DataField
              label=""
              value={<ActionButton onClick={setSixMonthsAgo}>{t('loan.createWizard.sixMonthsAgo')}</ActionButton>}
            />
          </FieldGrid>
        </Stack>
      </FieldsetCard>

      <FieldsetCard title={t('loan.createWizard.products')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('loan.createWizard.product')}</TableCell>
              <TableCell align="right" sx={{ width: 200 }}>
                {t('loan.createWizard.amount')}
              </TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map(
              (field, index) =>
                field.productId !== null && (
                  <TableRow key={field.id}>
                    <TableCell>{t(`enum.product.${field.productId}`)}</TableCell>
                    <TableCell align="right">
                      <ControlledCellNumberField
                        control={control}
                        name={`fundedProducts.${index}.amount`}
                        label={t('loan.createWizard.amount')}
                        isMoney
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      {/* The schema requires at least one product. */}
                      <DeleteIconButton size="small" disabled={fields.length === 1} onClick={() => remove(index)} />
                    </TableCell>
                  </TableRow>
                ),
            )}
          </TableBody>
        </Table>
      </FieldsetCard>
    </Stack>
  )
}

export default LoanParametersStep
